import { useState, useEffect, useRef, useCallback } from "react";
import { Loader2, Paperclip, Send, X, FileText, Download, Bot } from "lucide-react";
import api from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { toast } from "sonner";
import VoiceRecordButton from "../assistant/VoiceRecordButton";

const PUBLIC_BASE = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");

export const TYPE_LABELS = {
    problem: "Problem / Fehler",
    feature: "Erweiterungswunsch",
    support: "Support / Installation",
};
export const STATUS_LABELS = {
    open: "Offen", in_progress: "In Bearbeitung", resolved: "Gelöst", closed: "Geschlossen",
};
export const STATUS_COLORS = {
    open: "bg-amber-100 text-amber-800",
    in_progress: "bg-blue-100 text-blue-800",
    resolved: "bg-emerald-100 text-emerald-800",
    closed: "bg-gray-200 text-gray-600",
};
export const PRIORITY_LABELS = { low: "Niedrig", normal: "Normal", high: "Hoch" };
export const PRIORITY_COLORS = { low: "bg-gray-100 text-gray-600", normal: "bg-slate-100 text-slate-700", high: "bg-red-100 text-red-700" };

const fmtDate = (iso) => {
    if (!iso) return "";
    try { return new Date(iso).toLocaleString("de-DE", { dateStyle: "short", timeStyle: "short" }); } catch { return iso; }
};

/**
 * TicketThread — Konversationsverlauf eines Tickets + Antwort-Box mit Anhängen.
 * Rollenbewusst (Admin sieht Status-/Prioritäts-Steuerung).
 */
export default function TicketThread({ ticketId, onChanged }) {
    const { user } = useAuth();
    const isAdmin = user?.role === "admin";
    const [ticket, setTicket] = useState(null);
    const [loading, setLoading] = useState(true);
    const [body, setBody] = useState("");
    const [attachments, setAttachments] = useState([]);
    const [sending, setSending] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [aiThinking, setAiThinking] = useState(false);
    const fileRef = useRef(null);
    const endRef = useRef(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/tickets/${ticketId}`);
            setTicket(data);
        } catch { toast.error("Ticket konnte nicht geladen werden"); }
        finally { setLoading(false); }
    }, [ticketId]);

    // KI-Auto-Antwort anstoßen (nur solange kein Admin übernommen hat).
    const triggerAi = useCallback(async () => {
        setAiThinking(true);
        try {
            const { data } = await api.post(`/tickets/${ticketId}/ai-reply`, {});
            if (data?.ticket) setTicket(data.ticket);
        } catch { /* still fine */ }
        finally { setAiThinking(false); }
    }, [ticketId]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => { if (endRef.current) endRef.current.scrollIntoView({ block: "nearest" }); }, [ticket?.messages?.length, aiThinking]);

    // Beim Öffnen: wenn das letzte Wort beim Nutzer lag und die KI aktiv ist,
    // automatisch eine KI-Antwort erzeugen (z. B. direkt nach Ticket-Erstellung).
    const autoKicked = useRef(false);
    useEffect(() => {
        if (!ticket || autoKicked.current || isAdmin) return;
        const msgs = ticket.messages || [];
        const last = msgs[msgs.length - 1];
        if (ticket.ai_enabled !== false && ticket.status !== "closed" && last && last.author_role !== "assistant" && last.author_role !== "admin") {
            autoKicked.current = true;
            triggerAi();
        }
    }, [ticket, isAdmin, triggerAi]);

    const uploadFiles = async (files) => {
        const arr = Array.from(files || []);
        if (!arr.length) return;
        setUploading(true);
        for (const f of arr) {
            if (f.size > 10 * 1024 * 1024) { toast.error(`„${f.name}" ist größer als 10 MB.`); continue; }
            try {
                const fd = new FormData();
                fd.append("file", f);
                const { data } = await api.post("/tickets/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
                setAttachments((a) => [...a, data]);
            } catch { toast.error(`Upload von „${f.name}" fehlgeschlagen`); }
        }
        setUploading(false);
        if (fileRef.current) fileRef.current.value = "";
    };

    const send = async () => {
        if (!body.trim() && attachments.length === 0) return;
        setSending(true);
        try {
            const { data } = await api.post(`/tickets/${ticketId}/messages`, { body: body.trim(), attachments });
            setTicket(data);
            setBody("");
            setAttachments([]);
            onChanged?.();
        } catch { toast.error("Antwort konnte nicht gesendet werden"); }
        finally { setSending(false); }
    };

    const changeStatus = async (status) => {
        try {
            const { data } = await api.patch(`/tickets/${ticketId}`, { status });
            setTicket(data);
            onChanged?.();
        } catch (e) { toast.error(e.response?.data?.detail || "Status-Änderung fehlgeschlagen"); }
    };
    const changePriority = async (priority) => {
        try { const { data } = await api.patch(`/tickets/${ticketId}`, { priority }); setTicket(data); onChanged?.(); }
        catch { toast.error("Priorität konnte nicht geändert werden"); }
    };
    const [costDraft, setCostDraft] = useState("");
    useEffect(() => { if (ticket) setCostDraft(ticket.cost != null ? String(ticket.cost) : ""); }, [ticket?.id]);
    const saveCost = async () => {
        try {
            const { data } = await api.patch(`/tickets/${ticketId}`, { cost: costDraft === "" ? null : Number(costDraft) });
            setTicket(data); onChanged?.();
            toast.success(costDraft === "" ? "Betrag entfernt" : "Abrechnungsbetrag gespeichert");
        } catch (e) { toast.error(e.response?.data?.detail || "Betrag konnte nicht gespeichert werden"); }
    };

    if (loading) return <div className="flex items-center justify-center py-10 text-gray-400" data-testid="ticket-thread-loading"><Loader2 className="w-5 h-5 animate-spin" /></div>;
    if (!ticket) return null;

    return (
        <div className="flex flex-col h-full" data-testid="ticket-thread">
            <div className="flex items-start justify-between gap-3 pb-3 border-b border-gray-100">
                <div className="min-w-0">
                    <h3 className="font-display font-bold text-lg leading-tight truncate" data-testid="ticket-thread-subject">{ticket.subject}</h3>
                    <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-sm bg-[#111827] text-[#FFCC00]">{TYPE_LABELS[ticket.type]}</span>
                        <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-sm ${STATUS_COLORS[ticket.status]}`} data-testid="ticket-thread-status">{STATUS_LABELS[ticket.status]}</span>
                        <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-sm ${PRIORITY_COLORS[ticket.priority]}`}>Prio: {PRIORITY_LABELS[ticket.priority]}</span>
                        {isAdmin && <span className="text-[10px] text-gray-500">· {ticket.owner_name}</span>}
                    </div>
                </div>
                <div className="flex flex-col items-end gap-1.5 shrink-0">
                    {isAdmin ? (
                        <>
                            <select value={ticket.status} onChange={(e) => changeStatus(e.target.value)} className="text-xs border border-gray-300 rounded-sm px-2 h-8" data-testid="ticket-admin-status-select">
                                {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                            </select>
                            <select value={ticket.priority} onChange={(e) => changePriority(e.target.value)} className="text-xs border border-gray-300 rounded-sm px-2 h-8" data-testid="ticket-admin-priority-select">
                                {Object.entries(PRIORITY_LABELS).map(([k, v]) => <option key={k} value={k}>Prio: {v}</option>)}
                            </select>
                            <div className="flex items-center gap-1">
                                <input type="number" step="0.01" min="0" value={costDraft} onChange={(e) => setCostDraft(e.target.value)} placeholder="Betrag €" className="text-xs border border-gray-300 rounded-sm px-2 h-8 w-24 text-right" data-testid="ticket-admin-cost-input" title="Abrechnungsbetrag (SEPA, bei erledigtem Ticket)" />
                                <button onClick={saveCost} className="text-xs font-bold px-2 h-8 rounded-sm bg-[#111827] text-white hover:bg-black" data-testid="ticket-admin-cost-save">€</button>
                            </div>
                        </>
                    ) : (
                        ticket.status !== "closed"
                            ? <button onClick={() => changeStatus("closed")} className="text-xs font-bold px-2.5 h-8 rounded-sm border border-gray-300 hover:bg-gray-50" data-testid="ticket-close-btn">Ticket schließen</button>
                            : <button onClick={() => changeStatus("open")} className="text-xs font-bold px-2.5 h-8 rounded-sm border border-gray-300 hover:bg-gray-50" data-testid="ticket-reopen-btn">Wieder öffnen</button>
                    )}
                </div>
            </div>

            <div className="flex-1 overflow-y-auto py-3 space-y-3 min-h-[200px] max-h-[50vh]" data-testid="ticket-messages">
                {(ticket.messages || []).map((m) => {
                    const mine = m.author_id === user?.id;
                    const fromSupport = m.author_role === "admin";
                    return (
                        <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid="ticket-message">
                            <div className={`max-w-[80%] rounded-sm px-3 py-2 ${fromSupport ? "bg-[#111827] text-white" : mine ? "bg-emerald-50 border border-emerald-200" : "bg-gray-100"}`}>
                                <div className={`text-[10px] font-bold mb-1 ${fromSupport ? "text-[#FFCC00]" : "text-gray-500"}`}>
                                    {fromSupport ? "Support" : m.author_name} · {fmtDate(m.created_at)}
                                </div>
                                {m.body && <div className="text-sm whitespace-pre-wrap break-words">{m.body}</div>}
                                {(m.attachments || []).map((a, idx) => (
                                    <a key={idx} href={`${PUBLIC_BASE}${a.url}`} target="_blank" rel="noopener noreferrer" className={`inline-flex items-center gap-1 mt-1 text-[11px] font-bold underline ${fromSupport ? "text-[#FFCC00]" : "text-[#111827]"}`}>
                                        <FileText className="w-3 h-3" /> {a.name}
                                    </a>
                                ))}
                            </div>
                        </div>
                    );
                })}
                <div ref={endRef} />
            </div>

            {ticket.status !== "closed" ? (
                <div className="pt-3 border-t border-gray-100">
                    {attachments.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mb-2" data-testid="ticket-reply-attachments">
                            {attachments.map((a, i) => (
                                <span key={i} className="inline-flex items-center gap-1 text-[11px] bg-gray-100 rounded-sm px-2 py-1">
                                    <Download className="w-3 h-3" /> {a.name}
                                    <button onClick={() => setAttachments((x) => x.filter((_, idx) => idx !== i))} className="text-gray-400 hover:text-red-600"><X className="w-3 h-3" /></button>
                                </span>
                            ))}
                        </div>
                    )}
                    <div className="flex items-end gap-2">
                        <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={2} placeholder="Antwort schreiben …" className="flex-1 rounded-sm border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#111827] resize-none" data-testid="ticket-reply-input" />
                        <input ref={fileRef} type="file" multiple className="hidden" onChange={(e) => uploadFiles(e.target.files)} data-testid="ticket-reply-file-input" />
                        <VoiceRecordButton onText={(t) => setBody((b) => (b ? b + " " : "") + t)} disabled={sending} />
                        <button onClick={() => fileRef.current?.click()} disabled={uploading} className="h-10 w-10 flex items-center justify-center rounded-sm border border-gray-300 hover:bg-gray-50 shrink-0" title="Datei anhängen" data-testid="ticket-reply-attach-btn">
                            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Paperclip className="w-4 h-4" />}
                        </button>
                        <button onClick={send} disabled={sending || (!body.trim() && attachments.length === 0)} className="h-10 px-4 flex items-center gap-1.5 rounded-sm bg-[#111827] hover:bg-black text-white text-sm font-bold disabled:opacity-50 shrink-0" data-testid="ticket-reply-send-btn">
                            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Senden
                        </button>
                    </div>
                </div>
            ) : (
                <div className="pt-3 border-t border-gray-100 text-center text-xs text-gray-400" data-testid="ticket-closed-note">Dieses Ticket ist geschlossen.</div>
            )}
        </div>
    );
}
